import { useState } from 'react'
import { ArrowLeft, Loader2, Mail, ShieldCheck, Smartphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { useToast } from '@/hooks'
import type { TwoFactorMethod } from '@/types'

interface Props {
  method: TwoFactorMethod
  isVerifying: boolean
  onVerify: (code: string) => void
  onResend: () => void
  onCancel: () => void
}

/**
 * Segundo passo do login: entrada do código de 2FA. TOTP lê do app, email
 * recebe um OTP (com reenviar). TOTP também aceita um código de backup.
 */
export function TwoFactorChallenge({ method, isVerifying, onVerify, onResend, onCancel }: Props) {
  const { toast } = useToast()
  const [code, setCode] = useState('')
  const [useBackup, setUseBackup] = useState(false)

  const isEmail = method === 'email'

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const value = code.trim()
    if (value.length < 6) return
    onVerify(value)
  }

  function handleResend() {
    onResend()
    toast({ title: 'Código reenviado', description: 'Confira seu e-mail.' })
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="mx-auto w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          {useBackup ? (
            <ShieldCheck className="w-6 h-6 text-primary" />
          ) : isEmail ? (
            <Mail className="w-6 h-6 text-primary" />
          ) : (
            <Smartphone className="w-6 h-6 text-primary" />
          )}
        </div>
        <h2 className="text-xl font-bold text-foreground">Verificação em duas etapas</h2>
        <p className="text-sm text-muted-foreground">
          {useBackup
            ? 'Digite um dos seus códigos de backup.'
            : isEmail
              ? 'Enviamos um código de 6 dígitos para o seu e-mail.'
              : 'Abra seu app autenticador e digite o código de 6 dígitos.'}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        {useBackup ? (
          <div className="space-y-2">
            <Label htmlFor="backup">Código de backup</Label>
            <Input
              id="backup"
              autoFocus
              placeholder="xxxx-xxxx"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="h-12 bg-muted/30 border-border focus:border-primary text-center tracking-widest"
              disabled={isVerifying}
            />
          </div>
        ) : (
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={code}
              onChange={setCode}
              disabled={isVerifying}
              containerClassName="justify-center"
            >
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
        )}

        <Button
          type="submit"
          className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
          disabled={isVerifying || code.trim().length < 6}
        >
          {isVerifying ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Verificando...
            </>
          ) : (
            'Verificar'
          )}
        </Button>
      </form>

      <div className="space-y-3 text-center text-sm">
        {isEmail && !useBackup && (
          <button
            type="button"
            onClick={handleResend}
            disabled={isVerifying}
            className="text-muted-foreground hover:text-primary transition-colors"
          >
            Não recebeu? Reenviar código
          </button>
        )}
        {method === 'totp' && (
          <div>
            <button
              type="button"
              onClick={() => {
                setUseBackup((v) => !v)
                setCode('')
              }}
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              {useBackup ? 'Usar código do app' : 'Usar um código de backup'}
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Voltar ao login
        </button>
      </div>
    </div>
  )
}
